import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Box, Divider, HStack, Icon, Text } from "@chakra-ui/react";
import { createPortal } from "react-dom";
import { ArtifactSection } from "modules/artifacts/utils/artifactUtils";

export interface ArtifactMentionMenuHandle {
  /** Relay a keydown event from the chat input to drive arrow-key / Enter / Escape. */
  handleKeyDown: (e: React.KeyboardEvent) => void;
}

interface ArtifactMentionMenuProps {
  /** Sections from buildArtifactSections, already filtered by the parent's query. */
  sections: ArtifactSection[];
  /** Called when the user selects an item; receives the [[type: name]] tag string. */
  onSelect: (tag: string) => void;
  /** Called when the menu should close without a selection (Escape or empty results). */
  onClose: () => void;
  /** Ref to the element the menu is anchored above (the chat input wrapper). */
  anchorRef: React.RefObject<HTMLElement>;
}

/**
 * Floating sectioned menu that appears above the chat input when the user types '@'.
 *
 * Items are derived from the session's artifact map via buildMentionSections
 * (shared with ArtifactPanel's browse menu). The component is self-contained:
 * it owns its active-index state and exposes a `handleKeyDown` imperative method
 * so the parent can relay keyboard events without lifting extra state.
 */
const ArtifactMentionMenu = forwardRef<ArtifactMentionMenuHandle, ArtifactMentionMenuProps>(
  ({ sections, onSelect, onClose, anchorRef }, ref) => {
    const menuRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState(0);

    // Flatten all visible items for keyboard navigation.
    const allItems = sections.flatMap(s => s.items);

    // Reset active index whenever the filtered list changes.
    useEffect(() => {
      setActiveIndex(0);
    }, [sections]);

    // Keep the active item scrolled into view.
    useEffect(() => {
      const menu = menuRef.current;
      if (!menu) return;
      const active = menu.querySelector<HTMLElement>("[data-active='true']");
      active?.scrollIntoView({ block: "nearest" });
    }, [activeIndex]);

    // Expose keyboard handling to the parent so arrow keys / Enter / Escape
    // work even though the chat input owns browser focus.
    useImperativeHandle(ref, () => ({
      handleKeyDown(e: React.KeyboardEvent) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setActiveIndex(i => Math.min(i + 1, allItems.length - 1));
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setActiveIndex(i => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
          const item = allItems[activeIndex];
          if (item) {
            e.preventDefault();
            e.stopPropagation();
            onSelect(item.tag);
          }
        } else if (e.key === "Escape") {
          e.preventDefault();
          onClose();
        }
      },
    }));

    // Close when clicking outside the menu.
    useEffect(() => {
      const handleOutsideClick = (e: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
          onClose();
        }
      };
      document.addEventListener("mousedown", handleOutsideClick);
      return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, [onClose]);

    // Position the menu above the anchor element.
    const [pos, setPos] = useState<{ bottom: number; left: number; width: number } | null>(null);
    useEffect(() => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      setPos({
        bottom: window.innerHeight - rect.top + 4,
        left: rect.left,
        width: rect.width,
      });
    }, [anchorRef]);

    if (!pos || allItems.length === 0) return null;

    return createPortal(
      <Box
        ref={menuRef}
        position="fixed"
        bottom={`${pos.bottom}px`}
        left={`${pos.left}px`}
        width={`${pos.width}px`}
        maxH="300px"
        overflowY="auto"
        bg="white"
        borderRadius="md"
        border="1px solid"
        borderColor="gray.200"
        shadow="lg"
        zIndex={2000}
        py={1}
        // Prevent blur on the chat input when clicking inside the menu.
        onMouseDown={e => e.preventDefault()}
      >
        {sections.map((section, sIdx) => (
          <Box key={section.id}>
            {/* Section divider label */}
            <Text
              fontSize="2xs"
              fontWeight="800"
              color="purple.700"
              letterSpacing="0.08em"
              px={3}
              pt={sIdx === 0 ? 1 : 2}
              pb={0.5}
              mb={0}
              userSelect="none"
            >
              {section.label.toUpperCase()}
            </Text>
            <Divider my={0} />

            {section.items.map(item => {
              const globalIdx = allItems.findIndex(i => i.key === item.key);
              const isActive = globalIdx === activeIndex;
              return (
                <HStack
                  key={item.key}
                  data-active={isActive ? "true" : "false"}
                  px={3}
                  py={1}
                  spacing={2}
                  cursor="pointer"
                  bg={isActive ? "purple.50" : "transparent"}
                  _hover={{ bg: "purple.50" }}
                  onMouseEnter={() => setActiveIndex(globalIdx)}
                  onClick={() => onSelect(item.tag)}
                >
                  <Icon as={item.icon} boxSize={3.5} color="gray.500" flexShrink={0} />
                  <Text fontSize="sm" color="gray.900" mb={0} noOfLines={1} fontWeight="600">
                    {item.label}
                  </Text>
                </HStack>
              );
            })}
          </Box>
        ))}
      </Box>,
      document.body
    );
  }
);

ArtifactMentionMenu.displayName = "ArtifactMentionMenu";

export default ArtifactMentionMenu;
